'use client';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import Return from '@/shared/ui-composite/Game/ReturnFromGame';
import MCQ from './MCQ';
import Input from './Input';
import TilesMode from './TilesMode';
import useKanaStore from '@/features/Kana/store/useKanaStore';
import {
  clearAutoLearningHandoff,
  readAutoLearningHandoff,
  useAutoLearningStore,
  useStatsStore,
} from '@/features/Progress';
import { useShallow } from 'zustand/react/shallow';
import SessionStats from '@/shared/ui-composite/Game/SessionStats';
import SessionSummaryScreen from '@/shared/ui-composite/Game/SessionSummaryScreen';
import StreakMilestoneOverlay from '@/shared/ui-composite/Game/StreakMilestoneOverlay';
import { useRouter } from '@/core/i18n/routing';
import { finalizeSession, startSession } from '@/shared/utils/sessionHistory';
import { useMenuSelectorStore } from '@/shared/ui-composite/Menu/store/useMenuSelectorStore';
import useClassicSessionStore from '@/shared/store/useClassicSessionStore';
import {
  ENABLE_EVERY_QUESTION_AD_OVERLAY,
  shouldShowStreakMilestoneOverlay,
} from '@/shared/utils/game/streakMilestones';

const Game = () => {
  const {
    showStats,
    resetStats,
    recordDojoUsed,
    recordModeUsed,
    recordChallengeModeUsed,
    numCorrectAnswers,
    numWrongAnswers,
    currentStreak,
    stars,
    totalMilliseconds,
    correctAnswerTimes,
  } = useStatsStore(
    useShallow(state => ({
      showStats: state.showStats,
      resetStats: state.resetStats,
      recordDojoUsed: state.recordDojoUsed,
      recordModeUsed: state.recordModeUsed,
      recordChallengeModeUsed: state.recordChallengeModeUsed,
      numCorrectAnswers: state.numCorrectAnswers,
      numWrongAnswers: state.numWrongAnswers,
      currentStreak: state.currentStreak,
      stars: state.stars,
      totalMilliseconds: state.totalMilliseconds,
      correctAnswerTimes: state.correctAnswerTimes,
    })),
  );
  const gameMode = useKanaStore(state => state.selectedGameModeKana);
  const setGameMode = useKanaStore(state => state.setSelectedGameModeKana);
  const replaceGroups = useKanaStore(state => state.setKanaGroupIndices);
  const setAutoSelectionActive = useAutoLearningStore(
    state => state.setAutoSelectionActive,
  );
  const isAutoLearningSessionRef = useRef(false);
  const router = useRouter();
  const [isSelectionReady, setIsSelectionReady] = useState(false);
  const [view, setView] = useState<'playing' | 'summary'>('playing');
  const [activeMilestone, setActiveMilestone] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionNonce, setSessionNonce] = useState(0);
  const setActiveSessionId = useClassicSessionStore(
    state => state.setActiveSessionId,
  );
  const resetKanaSelection = useMenuSelectorStore(
    state => state.resetKanaSelection,
  );

  useEffect(() => {
    const handoff = readAutoLearningHandoff('kana');
    if (handoff) {
      isAutoLearningSessionRef.current = true;
      replaceGroups(
        handoff.sets.flatMap(set =>
          Array.from(
            { length: set.endIndex - set.startIndex },
            (_, offset) => set.startIndex + offset,
          ),
        ),
      );
      setGameMode(handoff.gameMode);
      setAutoSelectionActive('kana', true);
      clearAutoLearningHandoff();
    }
    setIsSelectionReady(true);
  }, [replaceGroups, setAutoSelectionActive, setGameMode]);

  const clearAutoLearningSelection = useCallback(() => {
    if (!isAutoLearningSessionRef.current) return;
    replaceGroups([]);
    setAutoSelectionActive('kana', false);
    isAutoLearningSessionRef.current = false;
  }, [replaceGroups, setAutoSelectionActive]);

  useEffect(() => {
    const handleHistoryNavigation = () => clearAutoLearningSelection();
    window.addEventListener('popstate', handleHistoryNavigation);
    return () =>
      window.removeEventListener('popstate', handleHistoryNavigation);
  }, [clearAutoLearningSelection]);

  useEffect(() => {
    if (view !== 'playing') return;
    const totalQuestionsAnswered = numCorrectAnswers + numWrongAnswers;
    if (
      shouldShowStreakMilestoneOverlay(currentStreak, totalQuestionsAnswered)
    ) {
      setActiveMilestone(
        ENABLE_EVERY_QUESTION_AD_OVERLAY
          ? totalQuestionsAnswered
          : currentStreak,
      );
    }
  }, [currentStreak, numCorrectAnswers, numWrongAnswers, view]);

  useEffect(() => {
    if (!isSelectionReady) return;
    resetStats();
    setActiveMilestone(null);
    // Track dojo and mode usage for achievements (Requirements 8.1-8.3)
    recordDojoUsed('kana');
    recordModeUsed(gameMode.toLowerCase());
    recordChallengeModeUsed('classic');
    startSession({
      sessionType: 'classic',
      dojoType: 'kana',
      gameMode: gameMode.toLowerCase(),
      route: '/kana/train',
    }).then(id => {
      setSessionId(id);
      setActiveSessionId(id);
    });
    // Intentionally keyed by nonce only to avoid resetting a live session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionNonce, isSelectionReady]);

  const handleQuit = async () => {
    const id =
      sessionId ??
      (await startSession({
        sessionType: 'classic',
        dojoType: 'kana',
        gameMode: gameMode.toLowerCase(),
        route: '/kana/train',
      }));
    await finalizeSession({
      sessionId: id,
      endedReason: 'manual_quit',
      endedAbruptly: true,
      correct: numCorrectAnswers,
      wrong: numWrongAnswers,
      bestStreak: currentStreak,
      stars,
    });
    setActiveSessionId(null);
    setView('summary');
  };

  const handleNewSession = () => {
    resetStats();
    resetKanaSelection();
    setSessionId(null);
    setActiveSessionId(null);
    setView('playing');
    setSessionNonce(prev => prev + 1);
  };

  if (!isSelectionReady) {
    return (
      <div className='flex min-h-[100dvh] items-center justify-center text-(--secondary-color)'>
        Preparing your learning session...
      </div>
    );
  }

  return (
    <>
      <div
        key={sessionNonce}
        className={clsx(
          'flex min-h-[100dvh] max-w-[100dvw] flex-col items-center gap-8 px-2 md:gap-12 md:px-0',
        )}
      >
        {showStats && <SessionStats />}
        <Return
          isHidden={showStats || view === 'summary'}
          gameMode={gameMode}
          onQuit={handleQuit}
        />
        {gameMode.toLowerCase() === 'pick' ? (
          <TilesMode isHidden={showStats || view !== 'playing'} />
        ) : gameMode.toLowerCase() === 'mcq' ? (
          <MCQ isHidden={showStats || view !== 'playing'} />
        ) : gameMode.toLowerCase() === 'tiles' ? (
          <TilesMode isHidden={showStats || view !== 'playing'} />
        ) : gameMode.toLowerCase() === 'type' ? (
          <Input isHidden={showStats || view !== 'playing'} />
        ) : gameMode.toLowerCase() === 'anti-type' ? (
          <Input isHidden={showStats || view !== 'playing'} isReverse={true} />
        ) : null}
      </div>
      <StreakMilestoneOverlay
        milestone={activeMilestone}
        onDismiss={() => setActiveMilestone(null)}
      />
      {view === 'summary' && (
        <SessionSummaryScreen
          correct={numCorrectAnswers}
          wrong={numWrongAnswers}
          bestStreak={currentStreak}
          stars={stars}
          totalTimeMs={totalMilliseconds}
          correctAnswerTimes={correctAnswerTimes}
          onNewSession={handleNewSession}
          onBackToSelection={() => {
            clearAutoLearningSelection();
            router.push('/kana');
          }}
        />
      )}
    </>
  );
};

export default Game;
